import {
    Col,
    Row,
    TableColumnsType,
    Tooltip,
    Button as ButtonAnt,
    Typography,
    Form,
    Select,
    App,
    InputNumber,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, SaveOutlined, PlusOutlined } from '@ant-design/icons';
import { Table } from '@/components/tables/table';
import { ComboFoodManagementCreate } from '@/components/forms/combo-food-management/create.form';
import { css } from '@emotion/react';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import { setMessages } from '@/redux/slices/app.slice';
import { PageURLs } from '@/utils/navigate';
import { useQuery } from '@/hooks/query.hook';
import { Button } from '@/components/button';
import {
    watchHealthNutritionProfiles,
    createDetailMeal,
    editDetailMeal,
    watchListComboFoodDetail,
    getComboFoodDetail,
    getProfileComboFoodDetail,
    watchMealLoading,
    deleteDetailMeal,
    getGroupFoodMealP,
    getGroupFoodMealC,
    getGroupFoodMealV,
    getGroupFoodMealF,
    getGroupFoodMealM,
    getGroupFoodMealO,
    watchFoodGroupP,
    watchFoodGroupC,
    watchFoodGroupV,
    watchFoodGroupF,
    watchFoodGroupM,
    watchFoodGroupO,
    watchMealPagination,
    resetHealthNutritionProfiles,
    resetMessages,
    watchComboFoodMessage
} from '@/redux/slices/combo-food.slice';
import { PermissionAllowMode, PermissionMode } from '@/enums/mode.enum';
import { FOOD_TYPE, MealMode, NutritionGroupMode } from '@/enums/food.enum';
import { Message } from '@/enums/message.enum';
import { AGE_ALLOW_TYPE } from '@/enums/age.enum';
import usePermission from '@/hooks/permission.hook';
// import { MEALS_PER_DAY_OPTIONS } from '@/utils/nutrition-columns.utils';
import { handlePositiveNumberInputKeyDown } from '@/utils/common';

type PageChangeParams = {
    page?: number;
    pageSize?: number;
    filter?: FilterProduct;
};

const nutrition = [
    {
        key: NutritionGroupMode.PROTEIN,
        name: 'Nhóm chất đạm',
        value: FOOD_TYPE.NHOM_CHAT_DAM,
        totalGroupItem: 3,
        index: 0
    },
    {
        key: NutritionGroupMode.CARBOHYDRATE,
        name: 'Nhóm chất bột đường',
        value: FOOD_TYPE.NHOM_CHAT_BOT_DUONG,
        totalGroupItem: 3,
        index: 3
    },
    {
        key: NutritionGroupMode.VEGETABLE,
        name: 'Nhóm rau củ quả',
        value: FOOD_TYPE.RAU_CU_QUA,
        totalGroupItem: 3,
        index: 6

    },
    {
        key: NutritionGroupMode.FRUIT,
        name: 'Nhóm trái cây',
        value: FOOD_TYPE.TRAI_CAY,
        totalGroupItem: 3,
        index: 9

    },
    {
        key: NutritionGroupMode.MILK,
        name: 'Nhóm sữa và các chế phẩm từ sữa',
        value: FOOD_TYPE.SUA,
        totalGroupItem: 1,
        index: 12

    },
    {
        key: NutritionGroupMode.OIL,
        name: 'Nhóm dầu/ mỡ',
        value: FOOD_TYPE.NHOM_GIAU_CHAT_BEO,
        totalGroupItem: 1,
        index: 13

    },
]

const totalMeal = [
    {
        key: MealMode.BREAKFAST,
        name: 'Bữa sáng',
        data: nutrition
    },
    {
        key: MealMode.LUNCH,
        name: 'Bữa trưa',
        data: nutrition
    },
    {
        key: MealMode.AFTERNOON_MEAL,
        name: 'Bữa chiều',
        data: nutrition
    },
    {
        key: MealMode.DINNER,
        name: 'Bữa tối',
        data: nutrition
    },
    {
        key: MealMode.EXTRA_MEAL,
        name: 'Bữa phụ',
        data: nutrition
    },
];

const mealClassMap = {
    [MealMode.BREAKFAST]: 'morningStyle',
    [MealMode.LUNCH]: 'lunchStyle',
    [MealMode.AFTERNOON_MEAL]: 'eveningStyle',
    [MealMode.DINNER]: 'dinnerStyle',
    [MealMode.EXTRA_MEAL]: 'extraStyle',
};

export default function MenuManagementTable() {
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const pagination = useAppSelector(watchMealPagination);
    const messageInfo = useAppSelector(watchComboFoodMessage);
    const healthNutritionProfiles = useAppSelector(watchHealthNutritionProfiles);
    const listComboFoodDetail = useAppSelector(watchListComboFoodDetail);
    const mealLoading = useAppSelector(watchMealLoading);
    const foodGroupP = useAppSelector(watchFoodGroupP);
    const foodGroupC = useAppSelector(watchFoodGroupC);
    const foodGroupV = useAppSelector(watchFoodGroupV);
    const foodGroupF = useAppSelector(watchFoodGroupF);
    const foodGroupM = useAppSelector(watchFoodGroupM);
    const foodGroupO = useAppSelector(watchFoodGroupO);
    const { creatable, updatable, deletable } = usePermission();

    const { modal } = App.useApp();
    const { push, mode, comboFoodId } = useQuery();
    const [nutritionProfileId, setNutritionProfileId] = useState<number>(0);
    const mealCount = useRef(0);

    const [created, setCreated] = useState(false);
    const [dataRecord, setRecordData] = useState<DataMeal[]>([]);

    const handlePageChange = useCallback(
        ({
            page = PAGINATION_PARAMS.offset,
            pageSize = PAGINATION_PARAMS.limit,
        }: PageChangeParams) => {
            const limitPage = dataRecord.length > 0 && dataRecord[0].mode === PermissionAllowMode.CREATE ? pageSize - 1 : pageSize;
            dispatch(getComboFoodDetail({
                profileID: nutritionProfileId,
                page: page,
                limit: limitPage
            }));
        },
        [dataRecord, dispatch, nutritionProfileId]
    );

    const handleDelete = useCallback(async (structureId: number) => {
        await dispatch(deleteDetailMeal(structureId));
        const remainingRecords = listComboFoodDetail.length - 1;
        if (remainingRecords === 0 && pagination.curPage > 1) {
            handlePageChange({
                page: pagination.curPage - 1,
                pageSize: pagination.limitPage,
            });
        } else {
            handlePageChange({
                page: pagination.curPage,
                pageSize: pagination.limitPage,
            });
        }
    }, [dispatch, handlePageChange, listComboFoodDetail.length, pagination.curPage, pagination.limitPage]);

    useEffect(() => {
        if (messageInfo) {
            dispatch(
                setMessages([
                    {
                        type: messageInfo.type,
                        message: messageInfo.message,
                        description: messageInfo.description
                    }
                ])
            );

            dispatch(resetMessages());
        }
    }, [dispatch, messageInfo]);

    const handleGoBack = useCallback(() => {
        push(PageURLs.ofComboFoodManagement());
        dispatch(resetHealthNutritionProfiles());
    }, [push, dispatch]);

    useEffect(() => {
        const fetchData = async () => {
            if (mode === PermissionMode.EDIT && comboFoodId) {
                setCreated(true);
                await dispatch(getComboFoodDetail({ profileID: +comboFoodId }));
                await dispatch(getProfileComboFoodDetail(+comboFoodId));
            }
        };
        fetchData();
    }, [dispatch, comboFoodId, mode, form]);

    const getAllowedMealKeysByAge = (age?: string): MealMode[] => {
        if (age === AGE_ALLOW_TYPE.DT01) {
            return [MealMode.LUNCH, MealMode.DINNER];
        }
        if (
            [
                AGE_ALLOW_TYPE.DT02,
                AGE_ALLOW_TYPE.DT08,
                AGE_ALLOW_TYPE.DT10,
                AGE_ALLOW_TYPE.DT11,
                AGE_ALLOW_TYPE.DT12,
                AGE_ALLOW_TYPE.DT13,
                AGE_ALLOW_TYPE.DT13_DT14,
                AGE_ALLOW_TYPE.DT14,
                AGE_ALLOW_TYPE.DT15
            ].includes(age as AGE_ALLOW_TYPE)
        ) {
            return [MealMode.BREAKFAST, MealMode.LUNCH, MealMode.DINNER, MealMode.EXTRA_MEAL];
        }
        return [
            MealMode.BREAKFAST,
            MealMode.LUNCH,
            MealMode.AFTERNOON_MEAL,
            MealMode.DINNER,
            MealMode.EXTRA_MEAL,
        ];
    };

    const handle = useCallback(async (modeMeal?: string, structureId: number = 0) => {
        const values = form.getFieldsValue();
        let hasValidItem = false;
        const data: Record<string, string | number>[] = [];
        const dataMeal: {
            MEAL_TYPE: string;
            MEAL_NUTRIENTS: {
                NUTRIENT_TYPE: string;
                FOOD_ID?: number;
                FOOD_NAME: string;
                WEIGHT_GRAM: number;
            }[];
        }[] = [];

        const age = healthNutritionProfiles?.AGE;
        const allowedMealKeys = getAllowedMealKeysByAge(age);

        for (const meal of totalMeal.filter(m => allowedMealKeys.includes(m.key))) {
            const mealNutrients: {
                NUTRIENT_TYPE: string;
                FOOD_ID?: number;
                FOOD_NAME: string;
                WEIGHT_GRAM: number;
                SEQUENCE: number;
            }[] = [];

            for (const nutri of nutrition) {
                const row: Record<string, string | number> = {};
                let dataSequence = nutri.index + 1;
                for (let i = 1; i <= nutri.totalGroupItem; i++) {
                    const index = i + nutri.index;
                    const foodKey = `F_${structureId}_${meal.key}_${nutri.key}_${index}`;
                    const weightKey = `W_${structureId}_${meal.key}_${nutri.key}_${index}`;

                    const foodValue = values[foodKey];
                    const weightValue = values[weightKey];
                    if (foodValue && weightValue !== '' && weightValue !== undefined && weightValue !== null) {
                        hasValidItem = true;
                        row[foodKey] = foodValue.key;
                        row[weightKey] = Number(weightValue);
                        mealNutrients.push({
                            NUTRIENT_TYPE: nutri.key,
                            FOOD_NAME: foodValue.label,
                            FOOD_ID: foodValue.value,
                            WEIGHT_GRAM: Number(weightValue),
                            SEQUENCE: dataSequence
                        });
                        dataSequence++;
                    }
                }
                data.push(row);
            }

            if (mealNutrients.length > 0) {
                dataMeal.push({
                    MEAL_TYPE: meal.key,
                    MEAL_NUTRIENTS: mealNutrients,
                });
            }
        }
        if (!hasValidItem) {
            dispatch(
                setMessages([
                    {
                        type: Message.ERROR,
                        message: "Vui lòng nhập ít nhất 1 thực phẩm và khối lượng tương ứng của thực phẩm ",
                    }
                ])
            );
            return;
        }
        const nutrientMeal: DetailMealNutritionProfile = {
            STRUCTURE_ID: modeMeal === PermissionAllowMode.EDIT ? structureId : undefined,
            FOOD_COMBO_HEALTH_NUTRITION_PROFILE: nutritionProfileId,
            NUMBER_MEAL_PERDAY: mealCount.current,
            MEALS: dataMeal,
        };

        if (modeMeal === PermissionAllowMode.CREATE) {
            await dispatch(createDetailMeal(nutrientMeal));
        } else if (modeMeal === PermissionAllowMode.EDIT) {
            await dispatch(editDetailMeal(nutrientMeal));
        }
        await dispatch(getComboFoodDetail({ profileID: nutritionProfileId }));
    }, [dispatch, form, healthNutritionProfiles?.AGE, nutritionProfileId]);

    function transformApiDataToTableFormat(apiData: DetailMealNutritionProfile[]): DataMeal[] {
        return apiData.map(profile => {
            const mealRows: Record<string, string | number | FoodSelectValue>[] = [];

            profile.MEALS.forEach(meal => {
                nutrition.forEach(nutri => {
                    const row: Record<string, string | number | FoodSelectValue> = {};
                    for (let i = 1; i <= nutri.totalGroupItem; i++) {
                        const nutrientData = meal.MEAL_NUTRIENTS.filter(n => n.NUTRIENT_TYPE === nutri.key);
                        const nutriItem = nutrientData[i - 1];
                        const foodKey = `F_${profile.STRUCTURE_ID}_${meal.MEAL_TYPE}_${nutri.key}_${i + nutri.index}`;
                        const weightKey = `W_${profile.STRUCTURE_ID}_${meal.MEAL_TYPE}_${nutri.key}_${i + nutri.index}`;
                        if (nutriItem) {
                            row[foodKey] = { value: nutriItem.FOOD_ID ?? '', label: nutriItem.FOOD_NAME };
                            row[weightKey] = Number(nutriItem.WEIGHT_GRAM);
                        }
                    }
                    mealRows.push(row);
                });
            });

            return {
                structureID: profile.STRUCTURE_ID,
                numberMealPerDay: profile.NUMBER_MEAL_PERDAY,
                data: mealRows,
            };
        });
    }

    const confirmDelete = useCallback((structureId: number) => {
        modal.confirm({
            title: 'Bạn có chắc chắn xóa thực đơn này?',
            content: (
                <em>
                    Dữ liệu không thể khôi phục
                </em>
            ),
            okText: 'Xóa',
            width: 450,
            cancelText: 'Hủy',
            onOk: () => handleDelete(structureId)
        });
    }, [handleDelete, modal]);

    useEffect(() => {
        if (listComboFoodDetail) {
            const transformedData = transformApiDataToTableFormat(listComboFoodDetail);

            const emptyRow: DataMeal = {
                mode: PermissionAllowMode.CREATE,
                structureID: 0,
                data: []
            };
            setRecordData(prev => {
                if (prev.length > 0 && prev[0].mode === PermissionAllowMode.CREATE) {
                    form.resetFields();
                    return [emptyRow, ...transformedData];
                }
                return [...transformedData];
            });
        } else {
            setRecordData(prev => {
                if (prev.length > 0 && prev[0].mode === PermissionAllowMode.CREATE) {
                    return [...prev.slice(0, 1)];
                }
                return [];
            });
        }
    }, [form, listComboFoodDetail])

    const handleAddMeal = () => {
        form.resetFields();
        setRecordData(prev => {
            if (prev.length > 0 && prev[0].mode === PermissionAllowMode.CREATE) {
                return prev;
            }

            const emptyRow: DataMeal = {
                mode: PermissionAllowMode.CREATE,
                structureID: 0,
                data: []
            };
            return [emptyRow, ...prev];
        });
    };

    useEffect(() => {
        dispatch(getGroupFoodMealP());
        dispatch(getGroupFoodMealC());
        dispatch(getGroupFoodMealV());
        dispatch(getGroupFoodMealF());
        dispatch(getGroupFoodMealM());
        dispatch(getGroupFoodMealO());
    }, [dispatch]);

    const handleSearchGroup = useCallback(async (foodGroup?: string, query: string = '') => {
        if (foodGroup === FOOD_TYPE.NHOM_CHAT_DAM) {
            await dispatch(getGroupFoodMealP(query));
        } else if (foodGroup === FOOD_TYPE.NHOM_CHAT_BOT_DUONG) {
            await dispatch(getGroupFoodMealC(query));
        } else if (foodGroup === FOOD_TYPE.RAU_CU_QUA) {
            await dispatch(getGroupFoodMealV(query));
        } else if (foodGroup === FOOD_TYPE.TRAI_CAY) {
            await dispatch(getGroupFoodMealF(query));
        } else if (foodGroup === FOOD_TYPE.SUA) {
            await dispatch(getGroupFoodMealM(query));
        } else if (foodGroup === FOOD_TYPE.NHOM_GIAU_CHAT_BEO) {
            await dispatch(getGroupFoodMealO(query));
        }
        return;
    }, [dispatch]);

    const handleFoodGroup = useCallback((foodGroup: string) => {
        if (foodGroup === FOOD_TYPE.NHOM_CHAT_DAM) {
            return foodGroupP.map((item) => ({
                label: item.NAME,
                value: item.ID
            }));
        } else if (foodGroup === FOOD_TYPE.NHOM_CHAT_BOT_DUONG) {
            return foodGroupC.map((item) => ({
                label: item.NAME,
                value: item.ID
            }));
        } else if (foodGroup === FOOD_TYPE.RAU_CU_QUA) {
            return foodGroupV.map((item) => ({
                label: item.NAME,
                value: item.ID
            }));
        } else if (foodGroup === FOOD_TYPE.TRAI_CAY) {
            return foodGroupF.map((item) => ({
                label: item.NAME,
                value: item.ID
            }));
        } else if (foodGroup === FOOD_TYPE.SUA) {
            return foodGroupM.map((item) => ({
                label: item.NAME,
                value: item.ID
            }));
        } else if (foodGroup === FOOD_TYPE.NHOM_GIAU_CHAT_BEO) {
            return foodGroupO.map((item) => ({
                label: item.NAME,
                value: item.ID
            }));
        }
        return;
    }, [foodGroupC, foodGroupF, foodGroupM, foodGroupO, foodGroupP, foodGroupV]);

    const allColumns: TableColumnsType<any> = useMemo(() => {
        return [
            {
                title: <span className="white-header-text">Thực đơn</span>,
                key: 'index',
                width: 100,
                render: (_, __, index) => (pagination.curPage - 1) * pagination.limitPage + index + 1,
                fixed: 'left',
            },
            {
                title: 'Thao tác',
                key: 'action',
                fixed: 'left',
                width: 100,
                className: 'border-black-column white-header-text',
                render: (_, record: Meal, rowIndex: number) => (
                    <Row gutter={4} wrap={false}>
                        {updatable && (
                            <Col>
                                <Tooltip title='Cập nhật'>
                                    <ButtonAnt
                                        type='primary'
                                        icon={record.mode === PermissionAllowMode.CREATE || record.mode === PermissionAllowMode.EDIT ? <SaveOutlined /> : <EditOutlined />}
                                        css={editButtonStyles}
                                        onClick={() => {
                                            if (record.mode === PermissionAllowMode.CREATE) {
                                                handle(record.mode ?? '');
                                            } else if (record.mode === PermissionAllowMode.EDIT) {
                                                handle(record.mode ?? '', record.structureID);
                                            } else {
                                                setRecordData(prev =>
                                                    prev.map((r, idx) =>
                                                        idx === rowIndex ? { ...r, mode: PermissionAllowMode.EDIT } : r
                                                    )
                                                );
                                            }
                                        }}
                                    />
                                </Tooltip>
                            </Col>
                        )}
                        {deletable && (
                            <Col>
                                <Tooltip title='Xoá'>
                                    <ButtonAnt
                                        type='primary'
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => {
                                            if (record.mode === PermissionAllowMode.CREATE) {
                                                setRecordData(prev => prev.filter(r => r.mode !== PermissionAllowMode.CREATE || r !== record));
                                            } else if (record.mode === PermissionAllowMode.EDIT) {
                                                setRecordData(prev =>
                                                    prev.map((r, idx) =>
                                                        idx === rowIndex ? { ...r, mode: undefined } : r
                                                    )
                                                );
                                            } else {
                                                if (record.structureID) {
                                                    confirmDelete(record.structureID);
                                                }
                                            }
                                        }}
                                    />
                                </Tooltip>
                            </Col>
                        )}
                    </Row>
                )
            },
            {
                title: 'Số bữa / ngày',
                dataIndex: 'NUMBER_MEAL_PERDAY',
                key: 'NUMBER_MEAL_PERDAY',
                align: 'center',
                width: 125,
                className: 'white-header-text',
                render: () => { return <span style={{ color: '#000000' }}>{mealCount.current}</span> }
                //   render: (_: any, record: any) => {
                //     console.log('recoord', record);
                //     const structureId = record.structureID ?? 0;
                //     const fieldName = `NUMBER_MEAL_PERDAY_${structureId}`;

                //     if (record.mode === PermissionAllowMode.CREATE || record.mode === PermissionAllowMode.EDIT) {
                //       if (form && record.mode === PermissionAllowMode.EDIT && record.numberMealPerDay) {
                //         form.setFieldValue(fieldName, record.numberMealPerDay);
                //       }
                //       return (
                //         <Form.Item
                //           name={fieldName}
                //           rules={[{ required: true, message: 'Vui lòng chọn số bữa/ngày!' }]}
                //         >
                //           <Select
                //             options={MEALS_PER_DAY_OPTIONS}
                //             onChange={(value) => {
                //               mealCount.current = value;
                //             }}
                //           />
                //         </Form.Item>
                //       );
                //     }
                //     return <span style={{ color: '#000000e0' }}>{record.numberMealPerDay}</span>;
                //   }
            },
            ...totalMeal.map((meal) => ({
                title: meal.name,
                key: meal.key,
                align: 'center' as any,
                className: mealClassMap[meal.key] || '',
                children: nutrition.map((item) => {
                    return {
                        title: item.name,
                        dataIndex: item.key,
                        key: item.key,
                        className: mealClassMap[meal.key] || '',
                        children: item.value === FOOD_TYPE.SUA || item.value === FOOD_TYPE.NHOM_GIAU_CHAT_BEO ?
                            [
                                {
                                    title: `Thực phẩm ${1 + item.index}`,
                                    dataIndex: `F_${meal.key}_${item.key}_${1 + item.index}`,
                                    key: 'FOOD_NAME',
                                    width: 125,
                                    className: mealClassMap[meal.key] || '',
                                    render: (_: any, record: DataMeal) => {
                                        const structureId = record.structureID ?? 0;
                                        const key = `F_${structureId}_${meal.key}_${item.key}_${1 + item.index}`;
                                        const value = record.data?.find(obj => key in obj)?.[key] ?? '';

                                        if (record.mode === PermissionAllowMode.CREATE || record.mode === PermissionAllowMode.EDIT) {
                                            if (form && record.mode === PermissionAllowMode.EDIT) {
                                                form.setFieldValue(key, value);
                                            }
                                            return (
                                                <Form.Item name={key} rules={[{ required: true, message: 'Vui lòng chọn thực phẩm!' }]}>
                                                    <Select
                                                        showSearch
                                                        filterOption={false}
                                                        onSearch={(value) => {
                                                            handleSearchGroup(item.value, value);
                                                        }}
                                                        onFocus={() => {
                                                            handleSearchGroup();
                                                        }}
                                                        options={handleFoodGroup(item.value)}
                                                        labelInValue
                                                    />
                                                </Form.Item>
                                            );
                                        }
                                        return (
                                            <span>{
                                                typeof value === 'object' && value !== null && 'label' in value
                                                    ? value.label
                                                    : value ?? ''
                                            }</span>
                                        );
                                    }
                                },
                                {
                                    title: `Khối lượng ${1 + item.index}`,
                                    dataIndex: `W_${meal.key}_${item.key}_${1 + item.index}`,
                                    key: 'WEIGHT_GRAM',
                                    width: 125,
                                    align: 'center',
                                    className: mealClassMap[meal.key] || '',
                                    render: (_: any, record: DataMeal) => {
                                        const structureId = record.structureID ?? 0;
                                        const key = `W_${structureId}_${meal.key}_${item.key}_${1 + item.index}`;
                                        const value = record.data?.find(obj => key in obj)?.[key] ?? '';

                                        if (record.mode === PermissionAllowMode.CREATE || record.mode === PermissionAllowMode.EDIT) {
                                            if (form && record.mode === PermissionAllowMode.EDIT) {
                                                form.setFieldValue(key, value);
                                            }
                                            return (
                                                <Form.Item name={key} rules={[{ required: true, message: 'Vui lòng nhập khối lượng!' }]}>
                                                    <InputNumber type='number' min={0} onKeyDown={handlePositiveNumberInputKeyDown} />
                                                </Form.Item>
                                            );
                                        }
                                        return (
                                            <span>{
                                                typeof value === 'object' && value !== null && 'label' in value
                                                    ? value.label
                                                    : value ?? ''
                                            }</span>
                                        );
                                    }
                                },
                            ]
                            : [
                                {
                                    title: `Thực phẩm ${1 + item.index}`,
                                    dataIndex: `F_${meal.key}_${item.key}_${1 + item.index}`,
                                    key: 'FOOD_NAME',
                                    width: 125,
                                    className: mealClassMap[meal.key] || '',
                                    render: (_: any, record: DataMeal) => {
                                        const structureId = record.structureID ?? 0;
                                        const key = `F_${structureId}_${meal.key}_${item.key}_${1 + item.index}`;
                                        const value = record.data?.find(obj => key in obj)?.[key] ?? '';

                                        if (record.mode === PermissionAllowMode.CREATE || record.mode === PermissionAllowMode.EDIT) {
                                            if (form && record.mode === PermissionAllowMode.EDIT) {
                                                form.setFieldValue(key, value);
                                            }
                                            return (
                                                <Form.Item name={key} rules={[{ required: true, message: 'Vui lòng chọn thực phẩm!' }]}>
                                                    <Select
                                                        showSearch
                                                        filterOption={false}
                                                        onSearch={(value) => {
                                                            handleSearchGroup(item.value, value);
                                                        }}
                                                        onFocus={() => {
                                                            handleSearchGroup();
                                                        }}
                                                        options={handleFoodGroup(item.value)}
                                                        labelInValue
                                                    />
                                                </Form.Item>
                                            );
                                        }
                                        return (
                                            <span>{
                                                typeof value === 'object' && value !== null && 'label' in value
                                                    ? value.label
                                                    : value ?? ''
                                            }</span>
                                        );
                                    }
                                },
                                {
                                    title: `Khối lượng ${1 + item.index}`,
                                    dataIndex: `W_${meal.key}_${item.key}_${1 + item.index}`,
                                    key: 'WEIGHT_GRAM',
                                    width: 125,
                                    align: 'center',
                                    className: mealClassMap[meal.key] || '',
                                    render: (_: any, record: DataMeal) => {
                                        const structureId = record.structureID ?? 0;
                                        const key = `W_${structureId}_${meal.key}_${item.key}_${1 + item.index}`;
                                        const value = record.data?.find(obj => key in obj)?.[key] ?? '';

                                        if (record.mode === PermissionAllowMode.CREATE || record.mode === PermissionAllowMode.EDIT) {
                                            if (form && record.mode === PermissionAllowMode.EDIT) {
                                                form.setFieldValue(key, value);
                                            }
                                            return (
                                                <Form.Item name={key} rules={[{ required: true, message: 'Vui lòng nhập khối lượng!' }]}>
                                                    <InputNumber type='number' min={0} onKeyDown={handlePositiveNumberInputKeyDown} />
                                                </Form.Item>
                                            );
                                        }
                                        return (
                                            <span>{
                                                typeof value === 'object' && value !== null && 'label' in value
                                                    ? value.label
                                                    : value ?? ''
                                            }</span>
                                        );
                                    }
                                },
                                {
                                    title: `Thực phẩm ${2 + item.index}`,
                                    dataIndex: `F_${meal.key}_${item.key}_${2 + item.index}`,
                                    key: 'FOOD_NAME',
                                    width: 125,
                                    className: mealClassMap[meal.key] || '',
                                    render: (_: any, record: DataMeal) => {
                                        const structureId = record.structureID ?? 0;
                                        const key = `F_${structureId}_${meal.key}_${item.key}_${2 + item.index}`;
                                        const value = record.data?.find(obj => key in obj)?.[key] ?? '';

                                        if (record.mode === PermissionAllowMode.CREATE || record.mode === PermissionAllowMode.EDIT) {
                                            if (form && record.mode === PermissionAllowMode.EDIT) {
                                                form.setFieldValue(key, value);
                                            }
                                            return (
                                                <Form.Item name={key} rules={[{ required: true, message: 'Vui lòng chọn thực phẩm!' }]}>
                                                    <Select
                                                        showSearch
                                                        filterOption={false}
                                                        onSearch={(value) => {
                                                            handleSearchGroup(item.value, value);
                                                        }}
                                                        onFocus={() => {
                                                            handleSearchGroup();
                                                        }}
                                                        options={handleFoodGroup(item.value)}
                                                        labelInValue
                                                    />
                                                </Form.Item>
                                            );
                                        }
                                        return (
                                            <span>{
                                                typeof value === 'object' && value !== null && 'label' in value
                                                    ? value.label
                                                    : value ?? ''
                                            }</span>
                                        );
                                    }
                                },
                                {
                                    title: `Khối lượng ${2 + item.index}`,
                                    dataIndex: `W_${meal.key}_${item.key}_${2 + item.index}`,
                                    key: 'WEIGHT_GRAM',
                                    width: 125,
                                    align: 'center',
                                    className: mealClassMap[meal.key] || '',
                                    render: (_: any, record: DataMeal) => {
                                        const structureId = record.structureID ?? 0;
                                        const key = `W_${structureId}_${meal.key}_${item.key}_${2 + item.index}`;
                                        const value = record.data?.find(obj => key in obj)?.[key] ?? '';

                                        if (record.mode === PermissionAllowMode.CREATE || record.mode === PermissionAllowMode.EDIT) {
                                            if (form && record.mode === PermissionAllowMode.EDIT) {
                                                form.setFieldValue(key, value);
                                            }
                                            return (
                                                <Form.Item name={key} rules={[{ required: true, message: 'Vui lòng nhập khối lượng!' }]}>
                                                    <InputNumber type='number' min={0} onKeyDown={handlePositiveNumberInputKeyDown} />
                                                </Form.Item>
                                            );
                                        }
                                        return (
                                            <span>{
                                                typeof value === 'object' && value !== null && 'label' in value
                                                    ? value.label
                                                    : value ?? ''
                                            }</span>
                                        );
                                    }

                                },
                                {
                                    title: `Thực phẩm ${3 + item.index}`,
                                    dataIndex: `F_${meal.key}_${item.key}_${3 + item.index}`,
                                    key: 'FOOD_NAME',
                                    width: 125,
                                    className: mealClassMap[meal.key] || '',
                                    render: (_: any, record: DataMeal) => {
                                        const structureId = record.structureID ?? 0;
                                        const key = `F_${structureId}_${meal.key}_${item.key}_${3 + item.index}`;
                                        const value = record.data?.find(obj => key in obj)?.[key] ?? '';

                                        if (record.mode === PermissionAllowMode.CREATE || record.mode === PermissionAllowMode.EDIT) {
                                            if (form && record.mode === PermissionAllowMode.EDIT) {
                                                form.setFieldValue(key, value);
                                            }
                                            return (
                                                <Form.Item name={key} rules={[{ required: true, message: 'Vui lòng chọn thực phẩm!' }]}>
                                                    <Select
                                                        showSearch
                                                        filterOption={false}
                                                        onSearch={(value) => {
                                                            handleSearchGroup(item.value, value);
                                                        }}
                                                        onFocus={() => {
                                                            handleSearchGroup();
                                                        }}
                                                        options={handleFoodGroup(item.value)}
                                                        labelInValue
                                                    />
                                                </Form.Item>
                                            );
                                        }
                                        return (
                                            <span>{
                                                typeof value === 'object' && value !== null && 'label' in value
                                                    ? value.label
                                                    : value ?? ''
                                            }</span>
                                        );
                                    }
                                },
                                {
                                    title: `Khối lượng ${3 + item.index}`,
                                    dataIndex: `W_${meal.key}_${item.key}_${3 + item.index}`,
                                    key: 'WEIGHT_GRAM',
                                    width: 125,
                                    align: 'center',
                                    className: mealClassMap[meal.key] || '',
                                    render: (_: any, record: DataMeal) => {
                                        const structureId = record.structureID ?? 0;
                                        const key = `W_${structureId}_${meal.key}_${item.key}_${3 + item.index}`;
                                        const value = record.data?.find(obj => key in obj)?.[key] ?? '';

                                        if (record.mode === PermissionAllowMode.CREATE || record.mode === PermissionAllowMode.EDIT) {
                                            if (form && record.mode === PermissionAllowMode.EDIT) {
                                                form.setFieldValue(key, value);
                                            }
                                            return (
                                                <Form.Item name={key} rules={[{ required: true, message: 'Vui lòng nhập khối lượng!' }]}>
                                                    <InputNumber type='number' min={0} onKeyDown={handlePositiveNumberInputKeyDown} />
                                                </Form.Item>
                                            );
                                        }
                                        return (
                                            <span>{
                                                typeof value === 'object' && value !== null && 'label' in value
                                                    ? value.label
                                                    : value ?? ''
                                            }</span>
                                        );
                                    }
                                },
                            ]
                    }
                })
            })),
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.curPage, pagination.limitPage, updatable, deletable, handle, confirmDelete, form, handleFoodGroup, handleSearchGroup]);

    const columns = useMemo(() => {
        return allColumns.filter((col) => {
            const allowedMealKeysForM0709 = [MealMode.LUNCH, MealMode.DINNER];
            const allowedMealKeysForM7270 = [MealMode.BREAKFAST, MealMode.LUNCH, MealMode.DINNER, MealMode.EXTRA_MEAL];
            const allowed5MealKeys = [MealMode.BREAKFAST, MealMode.LUNCH, MealMode.AFTERNOON_MEAL, MealMode.DINNER, MealMode.EXTRA_MEAL];

            if (healthNutritionProfiles || comboFoodId) {
                setNutritionProfileId(healthNutritionProfiles?.ID || +comboFoodId);
                if ([MealMode.BREAKFAST, MealMode.AFTERNOON_MEAL, MealMode.EXTRA_MEAL, MealMode.LUNCH, MealMode.DINNER].includes(col.key as MealMode)) {
                    if (healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT01) {
                        mealCount.current = 2;
                        return allowedMealKeysForM0709.includes(col.key as MealMode);
                    }
                    if (healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT03 ||
                        healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT03_DT04 ||
                        healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT04 ||
                        healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT05 ||
                        healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT06 ||
                        healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT06_DT07 ||
                        healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT07
                    ) {
                        mealCount.current = 5;
                        return allowed5MealKeys.includes(col.key as MealMode);
                    }
                    if (healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT02 ||
                        healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT08 ||
                        healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT10 ||
                        healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT11 ||
                        healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT12 ||
                        healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT13 ||
                        healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT13_DT14 ||
                        healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT14 ||
                        healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT15 ||
                        healthNutritionProfiles?.AGE === AGE_ALLOW_TYPE.DT09
                    ) {
                        mealCount.current = 4;
                        return allowedMealKeysForM7270.includes(col.key as MealMode);
                    }
                    mealCount.current = 5;
                    return true;
                }
                return true;
            }
        })
    }, [allColumns, comboFoodId, healthNutritionProfiles]);

    return (
        <div css={rootStyles}>
            <div css={boxStyles} style={{ marginTop: '1rem' }}>
                <Row align='middle' gutter={16}>
                    <Button
                        type='text'
                        icon={<ArrowLeftOutlined />}
                        onClick={handleGoBack}
                    >
                        {' '}
                        <Typography.Title level={5} style={{ marginBottom: 0 }}>
                            Tự xây dựng thực đơn chi tiết
                        </Typography.Title>
                    </Button>
                </Row>
            </div>

            <ComboFoodManagementCreate setCreated={setCreated} setRecordData={setRecordData} created={created} />
            {
                created && (
                    <>
                        {creatable && (
                            <div css={boxAddStyle}>
                                <Row align='middle' justify="end" gutter={16}>
                                    <Button
                                        icon={<PlusOutlined />}
                                        css={selfInputButtonStyles}
                                        onClick={() => handleAddMeal()}
                                    >
                                        Thêm thực đơn khác
                                    </Button>
                                </Row>
                            </div>
                        )}
                        <div css={boxStyles}>
                            <Form form={form}>
                                <Table<any>
                                    columns={columns}
                                    dataSource={dataRecord}
                                    loading={mealLoading}
                                    enableSticky
                                    stickyOffsetHeader={60}
                                    pagination={{
                                        showQuickJumper: false,
                                        showSizeChanger: true,
                                        current: pagination.curPage,
                                        pageSize: pagination.limitPage,
                                        total: pagination.totalRows,
                                        onChange: (page, pageSize) => {
                                            handlePageChange({ page, pageSize });
                                        }
                                    }}
                                    scroll={{ x: 500 }}
                                />
                            </Form>
                        </div>
                    </>
                )
            }
        </div>
    );
}

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxAddStyle = css`
  margin: 0 1rem;
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
`
const boxStyles = css`
  margin: 0 1rem;
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
    }

    .ant-table-thead > tr > th.morningStyle {
        background-color: var(--red-color) !important;
        color: var(--white-color);
    }

    .ant-table-thead > tr > th.lunchStyle {
        background-color: var(--blue-soft-color) !important;
        color: var(--white-color);
    }

  .ant-table-thead > tr > th.eveningStyle {
    background-color: var(--yellow-dark-color) !important;
    color: var(--white-color);
  }

  .ant-table-thead > tr > th.dinnerStyle {
    background-color: var(--red-color) !important;
    color: var(--white-color);
  }

  .ant-table-thead > tr > th.extraStyle {
    background-color: var(--blue-soft-color) !important;
    color: var(--white-color);
  }

  .ant-table-thead > tr > th {
    background: var(--blue-dark-color) !important;
  }

  .ant-table-cell::before {
    display: none;
  }

    .ant-table-cell.border-black-column {
        box-shadow: 0 0px 4px rgba(0,0,0,0.2);
        z-index: 1;
        position: relative;
    }

    .ant-table-thead > tr > th:not(.border-black-column) {
        border: 1px solid white !important;
    }
    
    .white-header-text {
        color: white !important;
    }
    
    .ant-form-item {
        margin-bottom: unset;
    }
`;

const editButtonStyles = css`
  background-color: var(--yellow-light-color) !important;
  color: var(--white-color);
`;

const selfInputButtonStyles = css`
  color: var(--blue-light-color);
  border-color: var(--blue-light-color);

  &:hover {
    background-color: var(--white-color) !important;
    color: var(--blue-light-color) !important;
    border-color: var(--blue-light-color) !important;
  }
`;